/**
 * The disclaimer toolbar's edits on the markdown it stores: bold, italic and links, the subset
 * the live chat widget renders. Pure functions over (text, selection), so the toolbar only has to
 * put the result back into the textarea.
 */

export interface TextSelection {
	start: number;
	end: number;
}

export interface FormattedText {
	text: string;
	selection: TextSelection;
}

/** Wraps the selection in `marker` (`**` bold, `*` italic), or inserts an empty pair at the caret. */
export const wrapSelection = (text: string, { start, end }: TextSelection, marker: string): FormattedText => {
	const selected = text.slice(start, end);
	const next = `${text.slice(0, start)}${marker}${selected}${marker}${text.slice(end)}`;
	const innerStart = start + marker.length;
	return { text: next, selection: { start: innerStart, end: innerStart + selected.length } };
};

/** Only absolute web links: a disclaimer link opens in the shopper's browser. */
export const isWebUrl = (value: string): boolean => {
	try {
		const url = new URL(value.trim());
		return url.protocol === 'https:' || url.protocol === 'http:';
	} catch {
		return false;
	}
};

/** Turns the selection into `[label](url)`. With nothing selected the URL is its own label. */
export const insertLink = (text: string, { start, end }: TextSelection, url: string): FormattedText => {
	const href = url.trim();
	const label = text.slice(start, end) || href;
	const link = `[${label}](${href})`;
	const next = `${text.slice(0, start)}${link}${text.slice(end)}`;
	const caret = start + link.length;
	return { text: next, selection: { start: caret, end: caret } };
};
